import WhatsAppConnect from '@/components/WhatsAppConnect';
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApi, errMsg, notifyRefresh } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { ImagePlus, RotateCcw, Landmark, ShieldCheck, CreditCard, QrCode, ReceiptText, CheckCircle2, Circle, ArrowRight } from "lucide-react";

const FIELDS = [
  ["clinic_name", "Nome da clínica"],
  ["dentist_name", "Nome do(a) dentista"],
  ["cro", "CRO"],
  ["phone", "Telefone do consultório"],
  ["pix_key", "Chave PIX (usada nas mensagens)"],
  ["address", "Endereço"],
];
const DEFAULTS = { primary_color: "#0284c7", accent_color: "#10b981", theme: "claro", logo_data: "" };
const validHex = (v) => /^#[0-9a-fA-F]{6}$/.test(v || "");

export const SettingsDialog = ({ open, onOpenChange }) => {
  const api = useApi();
  const s = useSettings();
  const [f, setF] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    // Initialize only when the dialog opens, not whenever settings context refreshes.
    if (open && !wasOpen.current) {
      setF({ ...DEFAULTS, ...Object.fromEntries(FIELDS.map(([k]) => [k, s[k] || ""])), ...Object.fromEntries(["primary_color", "accent_color", "theme", "logo_data"].map((k) => [k, s[k] || DEFAULTS[k]])) });
    }
    wasOpen.current = open;
  }, [open, s]);

  const save = async (e) => {
    e.preventDefault();
    if (!validHex(f.primary_color) || !validHex(f.accent_color)) {
      toast.error("Use cores no formato hexadecimal, por exemplo #0284c7.");
      return;
    }
    if (uploading || saving) return;
    setSaving(true);
    try {
      await api.put("/settings", f);
      const persisted = await api.get("/settings");
      const savedSettings = persisted?.data ?? persisted;
      if (savedSettings?.logo_data !== f.logo_data) {
        throw new Error("O logotipo não foi confirmado no servidor. Tente novamente.");
      }
      toast.success("Configurações e identidade visual salvas");
      notifyRefresh();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.message || errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(file.type)) {
      toast.error("Use uma imagem PNG, JPG, WEBP ou SVG.");
      e.target.value = "";
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 1 MB.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      if (!result.startsWith("data:image/")) {
        toast.error("A imagem selecionada não pôde ser processada.");
      } else {
        setF((prev) => ({ ...prev, logo_data: result }));
      }
      setUploading(false);
      e.target.value = "";
    };
    reader.onerror = () => { toast.error("Não foi possível ler a imagem."); setUploading(false); e.target.value = ""; };
    reader.readAsDataURL(file);
  };

  const clearDemo = async () => {
    if (!window.confirm("Remover todos os pacientes e lançamentos de exemplo? Seus dados reais não serão afetados.")) return;
    try {
      await api.delete("/demo-data");
      toast.success("Dados de exemplo removidos");
      notifyRefresh();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Configurações da clínica</DialogTitle></DialogHeader>
        <WhatsAppConnect />
        <form onSubmit={save} className="grid gap-4">
          <section className="grid gap-3 rounded-xl border border-slate-200 p-3">
            <h3 className="text-sm font-bold text-slate-800">Identidade visual</h3>
            <div className="flex items-center gap-3">
              <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {f.logo_data ? <img src={f.logo_data} alt="Prévia do logotipo" className="h-full w-full object-contain p-1" /> : <ImagePlus className="h-7 w-7 text-slate-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <Label htmlFor="clinic-logo">Logotipo da clínica</Label>
                <Input id="clinic-logo" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogo} className="mt-1" data-testid="settings-logo-input" />
                <p className="mt-1 text-xs text-slate-500">PNG, JPG, WEBP ou SVG · máximo 1 MB</p>
                {f.logo_data && <Button type="button" variant="ghost" size="sm" className="mt-1 h-7" onClick={() => setF({ ...f, logo_data: "" })}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Remover logo</Button>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label htmlFor="primary-color">Cor principal</Label><div className="flex gap-2"><Input id="primary-color" type="color" value={validHex(f.primary_color) ? f.primary_color : DEFAULTS.primary_color} onChange={(e) => setF({ ...f, primary_color: e.target.value })} className="h-10 w-12 p-1" /><Input value={f.primary_color || ""} onChange={(e) => setF({ ...f, primary_color: e.target.value })} placeholder="#0284c7" /></div></div>
              <div className="grid gap-1.5"><Label htmlFor="accent-color">Cor de destaque</Label><div className="flex gap-2"><Input id="accent-color" type="color" value={validHex(f.accent_color) ? f.accent_color : DEFAULTS.accent_color} onChange={(e) => setF({ ...f, accent_color: e.target.value })} className="h-10 w-12 p-1" /><Input value={f.accent_color || ""} onChange={(e) => setF({ ...f, accent_color: e.target.value })} placeholder="#10b981" /></div></div>
            </div>
            <div className="grid gap-1.5"><Label htmlFor="clinic-theme">Aparência</Label><select id="clinic-theme" value={f.theme || "claro"} onChange={(e) => setF({ ...f, theme: e.target.value })} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="claro">Claro</option><option value="escuro">Escuro</option></select><p className="text-xs text-slate-500">O modo escuro será aplicado integralmente em uma etapa posterior.</p></div>
          </section>
          {FIELDS.map(([k, label]) => <div key={k} className="grid gap-1.5"><Label htmlFor={`settings-${k}`}>{label}</Label><Input id={`settings-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}-input`} /></div>)}
          <section className="overflow-hidden rounded-2xl border border-sky-200 bg-white shadow-sm" data-testid="bank-integration-readiness" aria-label="Recebimentos bancários">
            <div className="bg-gradient-to-r from-sky-900 via-sky-800 to-teal-700 p-4 text-white">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-white/15 p-2.5"><Landmark className="h-6 w-6" aria-hidden="true" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-sky-100">Financeiro integrado</p>
                  <h3 className="mt-1 text-lg font-bold">Receba direto na conta da clínica</h3>
                  <p className="mt-1 text-xs leading-relaxed text-sky-100">Cada clínica poderá conectar seu banco ou provedor compatível, após a contratação e os testes.</p>
                </div>
              </div>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-semibold">
                <Circle className="h-2.5 w-2.5 fill-amber-300 text-amber-300" aria-hidden="true" /> Integração ainda não ativada
              </span>
            </div>
            <div className="space-y-4 p-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">Formas de recebimento previstas</p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {[[QrCode, "Pix"], [ReceiptText, "Boleto"], [CreditCard, "Cartão"]].map(([Icon, name]) => (
                    <div key={name} className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-2 py-3 text-center">
                      <Icon className="h-5 w-5 text-sky-700" aria-hidden="true" />
                      <span className="text-xs font-semibold text-slate-700">{name}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">A disponibilidade depende da API do banco ou provedor escolhido.</p>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-800">Como ativar futuramente</p>
                <ol className="mt-3 space-y-3">
                  {[
                    ["1", "Escolher o banco", "Verificaremos se oferece integração compatível."],
                    ["2", "Conectar com segurança", "A autorização será feita sem compartilhar senhas neste formulário."],
                    ["3", "Testar e liberar", "Validaremos cobranças e confirmações antes de ativar."],
                  ].map(([step, title, detail], index) => (
                    <li key={step} className="flex items-start gap-3">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-sky-100 text-xs font-bold text-sky-800">{step}</span>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-slate-800">{title}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{detail}</p>
                      </div>
                      {index === 2 ? <CheckCircle2 className="h-4 w-4 text-slate-300" aria-hidden="true" /> : <ArrowRight className="h-4 w-4 text-slate-300" aria-hidden="true" />}
                    </li>
                  ))}
                </ol>
              </div>
              <div className="flex items-start gap-2 rounded-xl bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-900">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>Os valores serão recebidos pela própria clínica. Nenhuma conta está conectada e nenhum pagamento automático está habilitado.</span>
              </div>
              <Button type="button" disabled className="w-full bg-sky-700 text-white" title="Disponível após a implementação e validação da integração bancária">
                Conectar banco · Em breve
              </Button>
              <p className="text-center text-xs text-slate-500">Não informe senhas, tokens ou dados bancários sensíveis nesta tela.</p>
            </div>
          </section>
          {s.has_demo && <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">O app contém pacientes de exemplo para demonstração.<Button type="button" variant="outline" size="sm" className="mt-2 w-full border-amber-300 bg-white" onClick={clearDemo} data-testid="settings-clear-demo-btn">Remover dados de exemplo</Button></div>}
          <DialogFooter><Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" className="bg-sky-600 hover:bg-sky-700" disabled={uploading || saving} data-testid="settings-save-btn">{saving ? "Salvando..." : "Salvar personalização"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
